import { config } from "../../config";
import {
  Box,
  FormControl,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  Divider,
  Typography,
} from "@mui/material";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import styles from "./ChatInterview.module.scss";
import { callAPI } from "../../services/interview.service";
import { useAuthToken } from "../../hooks/useAuthToken";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import TextConverter from "../../utils/TextConverter";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import {
  IQuestion,
  IChatResponse,
  IChatRequest,
  ICheckpointResponse,
} from "../../models/IQuestion";
import { IAnswerData } from "../../models/IAnswer";
import { QuestionStore } from "../../context/questionContext";
import { responseStore } from "../../context/responseContext";
import { useLanguage } from "../../context/languageContext";


const chatbotEndpoint ="https://fa-np-tl-interview-ai-services.azurewebsites.net/api";
  
//const chatbotEndpoint = "http://localhost:7071/api";

interface ChatInterviewProps {
  onAllQuestionsComplete?: (isComplete: boolean) => void;
  onQuestionProgress?: (currentIndex: number) => void;
}

// Mover CustomInputComponent fuera del componente principal
const CustomInputComponent = React.forwardRef<HTMLDivElement, any>(
  (props, ref) => (
    <div
      ref={ref}
      style={{
        height: "100%",
        overflowY: "auto",
        padding: "16px",
        width: "100%",
        scrollBehavior: "smooth",
        display: "flex",
        flexDirection: "column-reverse",
      }}
    >
      <div ref={props.chatBoxRef}>
        {props.chatHistory.map((message: any, index: number) =>
          props.renderChatMessage(message, index)
        )}
      </div>
    </div>
  )
);

CustomInputComponent.displayName = "CustomInputComponent";

// Componente memoizado para los mensajes del chat
interface ChatMessagesProps {
  chatHistory: Array<{ role: string; content: string; isComplete: boolean }>;
  renderChatMessage: (
    message: { role: string; content: string; isComplete: boolean },
    index: number
  ) => JSX.Element;
}
const ChatMessages = React.memo(function ChatMessages({
  chatHistory,
  renderChatMessage,
}: ChatMessagesProps) {
  return (
    <div>
      {chatHistory.map((message, index) => renderChatMessage(message, index))}
    </div>
  );
});

function ChatInterview({
  onAllQuestionsComplete,
  onQuestionProgress,
}: ChatInterviewProps) {
  const [chatHistory, setChatHistory] = useState<
    Array<{ role: string; content: string; isComplete: boolean }>
  >([]);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [isInterviewComplete, setIsInterviewComplete] =
    useState<boolean>(false);
  const [localQuestionIndex, setLocalQuestionIndex] = useState<number>(0);
  const [currentThreadId, setCurrentThreadId] = useState<string>("");
  const [isLoadingInitialHistory, setIsLoadingInitialHistory] =
    useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const interviewId = searchParams.get("interviewId");
  const invitationCode = searchParams.get("invitationCode");
  const [previousAnswer, setPreviousAnswer] = useState<string>("");
  const [isRecording, setIsRecording] = useState(false);
  const [hasTextChanged, setHasTextChanged] = useState<boolean>(false);
  const accessToken = useAuthToken();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [answer, setAnswer] = useState<string>("");
  const answerTextarea = useRef<HTMLDivElement>(null);
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const {
    interview,
    currentQuestion,
    questions,
    setCurrentQuestionIndex,
    setCurrentQuestion,
    sessionId,
  } = useContext(QuestionStore);

  const { responses, updateAnswer } = useContext(responseStore);

  const [currentAnswer, setCurrentAnswer] = React.useState("");

  const { currentLanguage } = useLanguage();

  // Función para cargar el historial inicial de una pregunta específica
  const loadQuestionHistory = async (
    question: IQuestion,
    questionIndex: number
  ) => {
    if (!sessionId) return null;

    const threadId = `${sessionId}_${question.Id}`;
    try {
      const options = {
        accessToken,
        method: "GET",
        endpoint: `${chatbotEndpoint}/checkpoints?thread_id=${threadId}`,
      };

      const response: ICheckpointResponse = await callAPI(options);
     
      if (
        response.last_checkpoint &&
        response.last_checkpoint.messages.length > 0
      ) {
        // Verificar explícitamente que status sea "success"
        const isComplete = response.status === "success";
      

        return {
          messages: response.last_checkpoint.messages,
          is_complete: response.last_checkpoint.is_complete,
          questionIndex,
          isComplete,
        };
      }
    } catch (error) {
      console.error(
        `Error loading history for question ${question.Id}:`,
        error
      );
    }
    return null;
  };

  useEffect(() => {
    let isMounted = true;

    const loadInitialHistory = async () => {
      if (
        !isMounted ||
        !interview ||
        !questions ||
        questions.length === 0 ||
        !sessionId
      )
        return;

      setIsLoadingInitialHistory(true);
      // Mostrar el mensaje de bienvenida
      const initialGreeting = t("general.admin.welcomechat", {
        interviewTitle: interview.Title,
      });
      const welcomeMessage = {
        role: "assistant",
        content: initialGreeting,
        isComplete: true,
      };

      let allMessages = [welcomeMessage];
      let currentQuestionIndex = 0;
      let allQuestionsComplete = true; // Flag para verificar si todas las preguntas están completas

      // Cargar el historial de todas las preguntas
      for (let i = 0; i < questions.length; i++) {
        if (!isMounted) return;

       
        // Primero agregamos la pregunta actual
        allMessages.push({
          role: "assistant",
          content: questions[i].QuestionText,
          isComplete: true,
        });

        const questionHistory = await loadQuestionHistory(questions[i], i);
       
        if (questionHistory) {
          // Agregar mensajes del historial si existen
          const historyMessages = questionHistory.messages
            .filter((msg) => msg.content !== questions[i].QuestionText)
            .map((msg) => ({
              ...msg,
              isComplete: true,
            }));

          allMessages = allMessages.concat(historyMessages);

          if (questionHistory.is_complete !== true) {
            // Si encontramos una pregunta incompleta
            allQuestionsComplete = false;
            currentQuestionIndex = i;
            if (isMounted) {
              setLocalQuestionIndex(i);
              setCurrentQuestionIndex(i);
              setCurrentQuestion(questions[i]);
              setCurrentThreadId(`${sessionId}_${questions[i].Id}`);
              onQuestionProgress?.(i);
            }
            break;
          }
        } else {
          // No hay historial para esta pregunta
          allQuestionsComplete = false;
          currentQuestionIndex = i;
          if (isMounted) {
            setLocalQuestionIndex(i);
            setCurrentQuestionIndex(i);
            setCurrentQuestion(questions[i]);
            setCurrentThreadId(`${sessionId}_${questions[i].Id}`);
            onQuestionProgress?.(i);
          }
          break;
        }
      }

      // Si todas las preguntas están completas, desactivar el input y notificar al padre
      if (allQuestionsComplete && isMounted) {
        setIsInterviewComplete(true);
        onAllQuestionsComplete?.(true);
        onQuestionProgress?.(questions.length - 1);
      }

  
      if (isMounted) {
        setChatHistory(allMessages);
        setIsLoadingInitialHistory(false);
        setIsInitialLoad(false);
      }
    };

    loadInitialHistory();

    return () => {
      isMounted = false;
    };
  }, [
    interview,
    questions,
    sessionId,
    t,
    onAllQuestionsComplete,
    onQuestionProgress,
  ]);

  useEffect(() => {
    setAnswer(transcript);
  }, [transcript]);


  const scrollToBottom = useCallback(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, scrollToBottom]);

  const handleSendMessage = async () => {
    handleStopRecording()
    setAnswer("");
    if (answer.trim() === "" || isStreaming || !sessionId) return;

    const newUserMessage = {
      role: "user",
      content: answer,
      isComplete: true,
    };

    // Agregar mensaje temporal del asistente para mostrar que está "escribiendo"
    const tempAssistantMessage = {
      role: "assistant",
      content: "...",
      isComplete: false,
    };

    setChatHistory((prev) => [...prev, newUserMessage, tempAssistantMessage]);
    setAnswer("");
    setIsStreaming(true);

    try {
      const currentQuestion = questions[localQuestionIndex];
      const requestBody: IChatRequest = {
        thread_id: currentThreadId,
        question: {
          question: currentQuestion.QuestionText,
          context: currentQuestion.Context || "",
          question_number: localQuestionIndex + 1,
          total_questions: questions.length,
        },
        user_data: {
          nombre: "",
        },
        user_response: answer,
        description: interview?.Description || "",
        language: currentLanguage,
      };

      const options = {
        accessToken,
        method: "POST",
        endpoint: `${chatbotEndpoint}/interview_chat?`,
        body: requestBody,
      };

      const response: IChatResponse = await callAPI(options);

      const newMessages = response.messages
        .filter((msg, index) => !(index === 0 && msg.role === "user"))
        .map((msg) => ({
          role: msg.role,
          content: msg.content,
          isComplete: true,
        }));

      await new Promise<void>((resolve) => {
        setChatHistory((prev) => {
          const withoutTemp = prev.slice(0, -1);
          const uniqueMessages = newMessages.filter(
            (newMsg) =>
              !withoutTemp.some(
                (existingMsg) =>
                  existingMsg.role === newMsg.role &&
                  existingMsg.content === newMsg.content
              )
          );
          resolve();
          return [...withoutTemp, ...uniqueMessages];
        });
      });

      if (response.is_complete === true) {
        const question = questions[localQuestionIndex];
        const threadId = `${sessionId}_${question.Id}`;
        try {
          const historyResponse: ICheckpointResponse = await callAPI({
            accessToken,
            method: "GET",
            endpoint: `${chatbotEndpoint}/checkpoints?thread_id=${threadId}`,
          });
          let responseText = "";
          if (
            historyResponse.last_checkpoint &&
            historyResponse.last_checkpoint.messages.length > 0
          ) {
            const mensajes = historyResponse.last_checkpoint.messages
              .filter((msg) => msg.content !== question.QuestionText)
              .map(
                (msg) =>
                  `${msg.role === "user" ? "Usuario" : "AI"}: ${msg.content}`
              )
              .join("\n");
            responseText = `Pregunta: ${question.QuestionText}\n${mensajes}`;
          }

          const answerData = {
            InvitationCode: invitationCode || "",
            SessionId: sessionId,
            ResponseText: responseText,
            InterviewQuestionId: question.Id,
          };
          await updateAnswer(question.Id, answerData);

          if (localQuestionIndex === questions.length - 1) {
            setIsInterviewComplete(true);
            onAllQuestionsComplete?.(true);
            onQuestionProgress?.(localQuestionIndex);
          } else {
            const nextQuestionIndex = localQuestionIndex + 1;
            if (nextQuestionIndex < questions.length) {
              const nextQuestion = questions[nextQuestionIndex];
              setLocalQuestionIndex(nextQuestionIndex);
              setCurrentQuestionIndex(nextQuestionIndex);
              setCurrentQuestion(nextQuestion);
              setCurrentThreadId(`${sessionId}_${nextQuestion.Id}`);
              onQuestionProgress?.(nextQuestionIndex);

              await new Promise<void>((resolve) => {
                const nextQuestionMessage = {
                  role: "assistant",
                  content: nextQuestion.QuestionText,
                  isComplete: true,
                };
                setChatHistory((prev) => {
                  resolve();
                  return [...prev, nextQuestionMessage];
                });
              });
            }
          }
        } catch (e) {
          console.error("Error guardando el historial de la pregunta:", e);
        }
      }
    } catch (error) {
      console.error("Error in chat:", error);
      // Reemplazar el mensaje temporal con el mensaje de error
      setChatHistory((prev) => {
        const withoutTemp = prev.slice(0, -1); // Eliminar el mensaje temporal
        return [
          ...withoutTemp,
          {
            role: "assistant",
            content:
              "Lo siento, encontré un error. Por favor, inténtalo de nuevo.",
            isComplete: true,
          },
        ];
      });
    } finally {
      setIsStreaming(false);
      if (inputRef.current) {
        inputRef.current.focus();
      }
      scrollToBottom();
    }
  };

  const renderChatMessage = useCallback(
    (
      message: { role: string; content: string; isComplete: boolean },
      index: number
    ) => {
      const isUser = message.role === "user";
      return (
        <Box
          key={index}
          sx={{
            display: "flex",
            justifyContent: isUser ? "flex-end" : "flex-start",
            alignItems: "flex-end",
            mb: 2,
          }}
        >
          {!isUser ? (
            <Avatar
              alt="AI"
              src="/ai-avatar.png"
              sx={{ width: 32, height: 32, mr: 1 }}
            />
          ) : (
            <Avatar
              alt="USER"
              src="/ai-avatar.png"
              sx={{ width: 32, height: 32, mr: 1 }}
            />
          )}
          {!message.isComplete ? (
            <Box
              sx={{
                backgroundColor: isUser ? "#7dafff" : "#f2f2f2",
                color: isUser ? "white" : "black",
                borderRadius: 3,
                px: 2,
                maxWidth: "70%",
              }}
            >
              <TextConverter text={message.content} />
              <CircularProgress size={10} style={{ marginLeft: "5px" }} />
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: isUser ? "#7dafff" : "#f2f2f2",
                color: isUser ? "white" : "black",
                borderRadius: 3,
                px: 2,
                maxWidth: "70%",
                textAlign: "start",
              }}
            >
              <TextConverter text={message.content} />
            </Box>
          )}
        </Box>
      );
    },
    []
  );

  const handleGoBack = () => {
    navigate("/my-dashboard");
  };

  const handleStartRecording = () => {
    if (!browserSupportsSpeechRecognition) {
      console.warn("Speech Recognition no soportado");
      return;
    }

    if (!listening) {
      resetTranscript();
      SpeechRecognition.startListening({
        language: currentLanguage? "es-CO":"en-US",
        continuous: true,
      });
    } else {
      SpeechRecognition.stopListening();
    }

    setIsRecording(true);
  };
  const handleStopRecording = () => {
    setIsRecording(false);
    SpeechRecognition.stopListening();
  };

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setAnswer(newValue);
    setHasTextChanged(newValue !== previousAnswer);
  };

  return (
    <section className={styles.container}>
      <div className={styles.container_center}>
        {/* Área de mensajes del chat */}
        <Box
          className={styles.chat}
          sx={{
            height: "75vh",
            overflowY: "auto",
            maxWidth: "100%",
            px: "32px",
          }}
          ref={chatBoxRef}
        >
          <ChatMessages
            chatHistory={chatHistory}
            renderChatMessage={renderChatMessage}
          />
        </Box>
        {/* Loader inicial */}
        {isInitialLoad && isLoadingInitialHistory ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100px",
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              {t("general.loadingmessages")}
            </Typography>
          </Box>
        ) : isInterviewComplete ? (
          <Box sx={{ height: "100px" }}></Box>
        ) : (
          // Input y controles debajo del chat
          <div
            className={styles.container_input}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: "8px 16px",
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "white",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              {listening ? (
                <IconButton
                  color="error"
                  onClick={handleStopRecording}
                  disabled={isInterviewComplete}
                >
                  <StopIcon />
                </IconButton>
              ) : (
                <IconButton
                  color="primary"
                  onClick={handleStartRecording}
                  disabled={isInterviewComplete}
                >
                  <MicIcon />
                </IconButton>
              )}
            </div>
            <Divider orientation="vertical" flexItem />
            <TextField
              inputRef={inputRef}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              variant="outlined"
              multiline
              inputProps={{ cols: 40 }}
              fullWidth
              placeholder="Start typing.."
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={isStreaming || isInterviewComplete}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
            />
            <div
              className={styles.icon}
              onClick={handleSendMessage}
              style={{
                opacity: isStreaming ? 0.5 : 1,
                cursor: isStreaming ? "not-allowed" : "pointer",
              }}
            >
              <SendOutlinedIcon color="primary" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default ChatInterview;
